// App.js
import React, { useContext, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, View, Text, StyleSheet } from 'react-native';
import { NavigationContainer, createNavigationContainerRef } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { Ionicons } from '@expo/vector-icons';

import * as Updates from 'expo-updates';
import { useKeepAwake } from 'expo-keep-awake';

import { AuthProvider, AuthContext } from './src/context/AuthContext';
import { NotificationsProvider, NotificationsContext } from './src/context/NotificationsContext';
import { ScannerProvider } from './src/context/ScannerContext';

// Tab Screens
import LoginScreen           from './src/screens/LoginScreen';
import HomeScreen            from './src/screens/HomeScreen';
import OdemelerScreen        from './src/screens/OdemelerScreen';
import RaporlarScreen        from './src/screens/RaporlarScreen';
import AyarlarScreen         from './src/screens/AyarlarScreen';

// Yönetim Stack Screens
import YonetimScreen         from './src/screens/YonetimScreen';
import CariTahsilatScreen    from './src/screens/yonetim/CariTahsilatScreen';
import MusteriYonetimScreen  from './src/screens/yonetim/MusteriYonetimScreen';
import YonetimRaporlarScreen from './src/screens/yonetim/YonetimRaporlarScreen';
import SistemAyarlariScreen  from './src/screens/yonetim/SistemAyarlariScreen';
import UrunYonetimScreen from './src/screens/yonetim/UrunYonetimScreen';
import OnlineMagazaScreen from './src/screens/yonetim/OnlineMagazaScreen';

// Online Siparişler için Socket Context
import { SocketProvider } from './src/context/SocketContext';
import OnlineSiparislerScreen from './src/screens/OnlineSiparislerScreen';

const Stack    = createNativeStackNavigator();
const Tab      = createBottomTabNavigator();
const YStack   = createNativeStackNavigator(); // Yönetim için ayrı stack
const navigationRef = createNavigationContainerRef();

// ── Yönetim Stack ────────────────────────────────────────────
function YonetimStackNavigator() {
  return (
    <YStack.Navigator
      screenOptions={{
        headerStyle:     { backgroundColor: '#fff' },
        headerTintColor: '#2c3e50',
        headerTitleStyle:{ fontWeight: '700' },
      }}
    >
      <YStack.Screen
        name="YonetimDashboard"
        component={YonetimScreen}
        options={{ headerShown: false }}  // kendi header'ı var
      />
      <YStack.Screen
        name="CariTahsilat"
        component={CariTahsilatScreen}
        options={{ title: 'Cari Tahsilat' }}
      />
      <YStack.Screen
        name="MusteriYonetim"
        component={MusteriYonetimScreen}
        options={{ title: 'Müşteri Yönetimi' }}
      />
      <YStack.Screen
        name="UrunYonetim"
        component={UrunYonetimScreen}
        options={{ title: 'Ürün Yönetimi' }} />
      <YStack.Screen
        name="OnlineMagaza"
        component={OnlineMagazaScreen}
        options={{ title: 'Online Mağaza' }} />
      <YStack.Screen
        name="YonetimRaporlar"
        component={YonetimRaporlarScreen}
        options={{ title: 'Detaylı Raporlar' }}
      />
      <YStack.Screen
        name="SistemAyarlari"
        component={SistemAyarlariScreen}
        options={{ title: 'Sistem Ayarları' }}
      />
    </YStack.Navigator>
  );
}

// ── Bottom Tab ───────────────────────────────────────────────
function MainTabNavigator() {
  const { userToken } = useContext(AuthContext);
  const userInfo  = userToken ? JSON.parse(userToken) : null;
  const userLevel = userInfo?.Level || 0;

  return (
    <Tab.Navigator
      screenOptions={({ route }) => ({
        headerShown: false,
        tabBarActiveTintColor:   'tomato',
        tabBarInactiveTintColor: 'gray',
        tabBarIcon: ({ focused, color, size }) => {
          const icons = {
            'Ana Sayfa': focused ? 'home'       : 'home-outline',
            'Ödemeler':  focused ? 'card'       : 'card-outline',
            'Hareketler':  focused ? 'bar-chart'  : 'bar-chart-outline',
            'Ayarlar':   focused ? 'settings'   : 'settings-outline',
            'Yönetim':   focused ? 'shield'     : 'shield-outline',
            'Online':     focused ? 'globe'      : 'globe-outline',
          };
          return <Ionicons name={icons[route.name]} size={size} color={color} />;
        },
      })}
    >
      <Tab.Screen name="Ana Sayfa" component={HomeScreen} />
      <Tab.Screen name="Online" component={OnlineSiparislerScreen} />
      <Tab.Screen name="Ödemeler"  component={OdemelerScreen} />
      <Tab.Screen name="Hareketler"  component={RaporlarScreen} />
      <Tab.Screen name="Ayarlar"   component={AyarlarScreen} />
      {userLevel >= 2 && (
        <Tab.Screen
          name="Yönetim"
          component={YonetimStackNavigator}  // Stack'i bağla
        />
      )}
    </Tab.Navigator>
  );
}

// ── App Navigation ───────────────────────────────────────────
function AppNavigation() {
  const { isLoading, userToken } = useContext(AuthContext);
  const { notificationResponse } = useContext(NotificationsContext);
  const lastHandledNotificationRef = useRef(null);

  useEffect(() => {
    if (!notificationResponse || !navigationRef.isReady()) {
      return;
    }

    const identifier = notificationResponse.notification?.request?.identifier || 'unknown';
    const actionIdentifier = notificationResponse.actionIdentifier || 'default';
    const handledKey = `${identifier}:${actionIdentifier}`;

    if (lastHandledNotificationRef.current === handledKey) {
      return;
    }

    const data = notificationResponse.notification?.request?.content?.data || {};
    if (data.type === 'new-order' || data.type === 'manual-test') {
      navigationRef.navigate('MainTabs', {
        screen: 'Online',
        params: {
          openOrderId: data.siparisId ? String(data.siparisId) : undefined,
        },
      });
      lastHandledNotificationRef.current = handledKey;
    }
  }, [notificationResponse]);

  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color="tomato" />
      </View>
    );
  }

  return (
    <ScannerProvider navigationRef={navigationRef}>
      <NavigationContainer ref={navigationRef}>
        <Stack.Navigator screenOptions={{ headerShown: false }}>
          {userToken !== null ? (
            <Stack.Screen name="MainTabs" component={MainTabNavigator} />
          ) : (
            <Stack.Screen name="Login" component={LoginScreen} />
          )}
        </Stack.Navigator>
      </NavigationContainer>
    </ScannerProvider>
  );
}

// ── Root ─────────────────────────────────────────────────────
export default function App() {
  useKeepAwake();
  const [updateMsg, setUpdateMsg] = useState('');

  useEffect(() => {
    async function onFetchUpdateAsync() {
      try {
        const update = await Updates.checkForUpdateAsync();
        if (update.isAvailable) {
          setUpdateMsg('Güncelleme indiriliyor...');
          await Updates.fetchUpdateAsync();
          setUpdateMsg('Güncelleme hazır, lütfen kapat-aç yapın.');
        }
      } catch (error) {
        console.log("Update Check Error:", error);
      }
    }
    if (!__DEV__) onFetchUpdateAsync();
  }, []);

  return (
    <AuthProvider>
      <NotificationsProvider>
        <SocketProvider>
          <View style={{ flex: 1 }}>
            <AppNavigation />
            <View style={styles.footer}>
              <Text style={styles.versionText}>
                Runtime: {Updates.runtimeVersion || '1'} | {Updates.updateId ? 'Güncel' : 'Orijinal'}
              </Text>
              {updateMsg !== '' && (
                <Text style={styles.updateText}>{updateMsg}</Text>
              )}
            </View>
          </View>
        </SocketProvider>
      </NotificationsProvider>
    </AuthProvider>
  );
}

const styles = StyleSheet.create({
  footer: {
    position: 'absolute', bottom: 0, width: '100%',
    backgroundColor: 'rgba(255,255,255,0.5)',
    paddingBottom: 5, alignItems: 'center',
  },
  versionText: { fontSize: 10, color: '#999' },
  updateText:  { fontSize: 10, color: 'tomato', fontWeight: 'bold' },
});